import type { JSX } from 'react';
import { Orb } from '../shell/orb';
import { CodeStep } from './code-step';
import { EmailStep } from './email-step';
import { useSignIn } from './use-sign-in';

const COPY = {
  email: {
    title: 'Sign in to Trucklane Pilot',
    sub: 'We send a one-time code. No passwords to manage.',
    button: 'Send code',
    busy: 'Sending code…',
  },
  code: {
    title: 'Check your email',
    sub: 'We sent a six-digit code. It expires in 10 minutes.',
    button: 'Verify and continue',
    busy: 'Verifying…',
  },
} as const;

/** The full-screen sign-in (design L699–747): email, then the six-digit code. */
export function SignInOverlay(): JSX.Element {
  const signIn = useSignIn();
  const copy = COPY[signIn.step];

  return (
    <div className="bg-bg fixed inset-0 z-[80] grid place-items-center overflow-auto p-6">
      <div className="grid-backdrop [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,black,transparent)]" />
      <div className="animate-rise relative flex w-[min(420px,100%)] flex-col items-center gap-[26px]">
        <div className="flex flex-col items-center gap-3.5 text-center">
          <Orb size={56} />
          <div>
            <div className="text-2xl font-semibold tracking-[-.02em]">{copy.title}</div>
            <div className="text-mute mt-1.5 text-sm leading-normal">{copy.sub}</div>
          </div>
        </div>

        <form
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            signIn.submit();
          }}
          className="bg-surf shadow-panel flex w-full flex-col gap-4 rounded-[18px] border p-6"
        >
          {signIn.step === 'email' ? (
            <EmailStep field={signIn.emailField} />
          ) : (
            <CodeStep signIn={signIn} />
          )}

          {/* Empty unless Cloudflare needs a click; then its widget draws here. */}
          <div ref={signIn.captchaRefs.code} className="empty:hidden" />
          <div ref={signIn.captchaRefs.verify} className="empty:hidden" />

          {signIn.error !== '' && (
            <div role="alert" className="text-bad flex items-center gap-2 text-[12.5px]">
              <span className="bg-bad size-[5px] rounded-full" />
              {signIn.error}
            </div>
          )}

          <button
            type="submit"
            className="w-full rounded-[11px] border-0 p-[13px] text-sm font-semibold transition-colors"
            style={{
              background: signIn.busy ? 'var(--surf2)' : 'var(--acc)',
              color: signIn.busy ? 'var(--mute)' : 'var(--accInk)',
            }}
          >
            {signIn.busy ? copy.busy : copy.button}
          </button>

          {signIn.step === 'code' && (
            <button
              type="button"
              onClick={signIn.back}
              className="text-mute border-0 bg-transparent p-0 text-[12.5px]"
            >
              ← Use a different email
            </button>
          )}
        </form>

        <div className="text-mute text-center font-mono text-[10.5px] leading-[1.7] tracking-[.08em]">
          ADMIN ACCESS · TRUCKLANE PILOT
          <br />
          Only allowed users can sign in. Ask an admin for access.
        </div>
      </div>
    </div>
  );
}
